import React, { useCallback, useMemo, useState } from 'react';
import { FormErrors, SubmissionError } from 'redux-form';
import styled from 'styled-components/macro';
import { useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import { PurchaseGalleryDialog } from '../PurchaseGallery/PurchaseGalleryDialog';
import { ApiClient } from 'src/client/helpers';
import urls, { constructUrl } from 'src/shared/urls';
import { purchase as cbpPurchase } from 'src/client/redux/modules/PPSInfo';
import { MoviePurchaseOptionsModal } from 'src/client/components/modals/MoviePurchaseOptionsModal';
import { secondsToRuntime } from 'src/client/utils/converters';
import { MovieRegularPurchasePanel } from 'src/client/components/panels/MovieRegularPurchasePanel';
import { MovieStaffSelectionPurchasePanel } from 'src/client/components/panels/MovieStaffSelectionPurchasePanel';
import { useSelector } from 'src/client/redux';
import { BillerById } from 'src/shared/constants/billers';
import { addDashesToUUID, generatePurchaseProduct } from 'src/shared/converters';
import { MetartIntimatePurchasePanel } from 'src/client/components/panels/MetartIntimatePurchasePanel';
import { Movie } from 'src/@types/Movie';
import { Site } from 'src/client/redux/modules/site';
import { MAM_COOKIE_NAME } from 'src/shared/constants/cookies';
import { PurchaseOption } from '../../components/dropdowns/DownloadDropdown/PurchaseDownloadDropdownMenu';
import { ResponseError } from 'superagent';
import { useGetGalleryCoverUrl } from 'src/client/helpers/urls/useGetGalleryCoverUrl';

const QualityMap = {
  UHD: '4k',
  HD: '1080p',
  SD: '720p',
};

type Props = {
  className?: string;
  gallery: Movie;
};

export default function MoviePurchasePanel(props: Props) {
  const { gallery } = props;

  const dispatch = useDispatch();

  const ppsInfo = useSelector((state) => state.PPSInfo);
  const user = useSelector((state) => state.auth.user);
  const site = useSelector((state) => state.site);
  const {
    config,
    contentCdnUrl,
    ssoUrl,
    config: { hasPPS, has4kResolution },
    test: abTest,
  } = useSelector((state) => state.app);

  const [cookies] = useCookies([MAM_COOKIE_NAME, `${site.abbreviation}_PA`, `_vis_opt_exp_${abTest?.testId}_combi`]);
  const mamPartnerID = useMemo(() => (cookies.mam ? cookies.mam.split('.')[0] : null), [cookies.mam]);
  const affiliateID = cookies[`${site.abbreviation}_PA`];
  const variant = parseInt(cookies[`_vis_opt_exp_${abTest?.testId}_combi`]);
  const getGalleryCover = useGetGalleryCoverUrl();

  let customPrices;
  if (abTest?.pps && variant) {
    customPrices = abTest.pps[variant - 1];
  }

  const affiliateCode = useMemo(() => {
    if (mamPartnerID) {
      return `mam-${mamPartnerID}`;
    }

    return affiliateID;
  }, [affiliateID, mamPartnerID]);

  const [purchaseModalSettings, setPurchaseModalSettings] = useState<
    | false
    | {
        price: number;
        title: string;
        quality: string;
      }
  >(false);
  const [purchasing, setPurchasing] = useState(false);
  const [showPurchaseOptionsModal, setShowPurchaseOptionsModal] = useState(false);

  // Price for button
  const priceOptions = useMemo(
    () =>
      ['UHD', 'HD', 'SD'].reduce((acc, quality) => {
        const product = generatePurchaseProduct(gallery.UUID, quality, '', customPrices, gallery.isStaffSelection); // siteUUID doesn't matter here
        if (product) {
          const option = QualityMap[quality];
          acc[option] = {
            price: parseFloat(product.initialPrice),
            title: `${option} ${quality}`,
            quality,
          };
        }
        return acc;
      }, {}),
    [customPrices, gallery.UUID, gallery.isStaffSelection],
  );

  const purchaseOptions = useMemo(
    () =>
      gallery.files.sizes.videos
        .filter((purchaseOption) => !!priceOptions[purchaseOption.id])
        .map((download) => ({
          ...priceOptions[download.id],
          id: download.id,
          size: download.size,
        })),
    [gallery.files.sizes.videos, priceOptions],
  );

  const chargeByPreviousPurchase = useCallback(async () => {
    setPurchasing(true);

    if (!purchaseModalSettings) {
      return;
    }

    const params = {
      affiliateCode,
      galleryName: gallery.name,
      galleryUUID: gallery.UUID,
      isStaffSelection: gallery.isStaffSelection,
      quality: purchaseModalSettings.quality,
      siteName: site.name,
      siteUUID: site.UUID,
      type: 'movie',
    };

    dispatch(cbpPurchase(params));
  }, [
    affiliateCode,
    dispatch,
    gallery.isStaffSelection,
    gallery.name,
    gallery.UUID,
    site.name,
    site.UUID,
    purchaseModalSettings,
  ]);

  const purchaseScene = useCallback(
    async ({ email, password }) => {
      setPurchasing(true);

      if (!purchaseModalSettings) {
        throw new SubmissionError({ email: 'Invalid Purchase Option' } as FormErrors<{ email: string }>);
      }

      const data = {
        email,
        galleryUUID: gallery.UUID,
        password,
        quality: purchaseModalSettings.quality,
        siteUUID: gallery.siteUUID,
      };

      try {
        const api = new ApiClient();
        const purchaseEndpoint = constructUrl(urls.post.purchase.scene);

        const {
          body: { billerID, cartItem, hash, url },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } = await (api as any).post(purchaseEndpoint, { data });

        const iframeURL = cartItem.iframeURL.trim();
        const redirectURL = cartItem.redirectURL.trim();

        window.dataLayer.push({
          event: 'begin_checkout',
          ecommerce: {
            affiliate_id: affiliateCode,
            source: 'PPS Dialog',
            value: cartItem.product.initialPrice,
            tax: '',
            currency: cartItem.product.currency,
            payment_method: BillerById[billerID],
            cbp: 0,
            items: [
              {
                item_name: gallery.name,
                item_id: cartItem.product.productUUID,
                item_brand: getItemBrand(gallery, site),
                item_category: 'pps',
                item_category2: purchaseModalSettings.quality,
                item_list_name: 'Films',
                item_list_id: addDashesToUUID(gallery.UUID),
                quantity: 1,
                price: cartItem.product.initialPrice,
              },
            ],
          },
        });
        window.dataLayer.push({
          event: 'begin_checkout_ua',
          ecommerce: {
            currency: cartItem.product.currency,
            checkout: {
              actionField: {
                affiliate_id: affiliateCode,
                source: 'PPS Dialog',
                value: cartItem.product.initialPrice,
                tax: '',
                option: BillerById[billerID],
                cbp: 0,
                product_uuid: cartItem.product.productUUID,
                category2: purchaseModalSettings.quality,
              },
              products: [
                {
                  name: gallery.name,
                  id: addDashesToUUID(gallery.UUID),
                  brand: getItemBrand(gallery, site),
                  category: 'pps',
                  variant: 'Films',
                  quantity: 1,
                  price: cartItem.product.initialPrice,
                },
              ],
            },
          },
        });

        window.location.href = `${ssoUrl}/login/${hash}?continue=${url}&cartItemUUID=${cartItem.cartItemUUID}&${
          iframeURL ? `iframeURL=${encodeURIComponent(iframeURL)}` : `redirectURL=${encodeURIComponent(redirectURL)}`
        }`;
      } catch (err: unknown) {
        const error = err as ResponseError;

        setPurchasing(false);
        let message = 'Submission failed, please try again later';

        if (error?.response?.status === 409) {
          message = 'Please login to complete this purchase';
        }

        throw new SubmissionError({ email: message } as FormErrors<{
          email: string;
        }>);
      }
    },
    [affiliateCode, gallery, purchaseModalSettings, site, ssoUrl],
  );

  const purchase = useCallback(
    async (purchaseItem: PurchaseOption) => {
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          affiliate_id: affiliateCode,
          source: 'PPS Dialog',
          currency: 'USD',
          items: [
            {
              item_name: gallery.name,
              item_brand: getItemBrand(gallery, site),
              item_category: 'pps',
              item_category2: purchaseItem.quality,
              item_list_name: 'Films',
              item_list_id: addDashesToUUID(gallery.UUID),
              quantity: 1,
              price: purchaseItem.price,
            },
          ],
        },
      });
      window.dataLayer.push({
        event: 'select_item_ua',
        ecommerce: {
          currency: 'USD',
          click: {
            actionField: {
              affiliate_id: affiliateCode,
              source: 'PPS Dialog',
              category2: purchaseItem.quality,
            },
            products: [
              {
                name: gallery.name,
                brand: getItemBrand(gallery, site),
                category: 'pps',
                id: addDashesToUUID(gallery.UUID),
                quantity: 1,
                price: purchaseItem.price,
                variant: 'Films',
              },
            ],
          },
        },
      });

      setShowPurchaseOptionsModal(false);

      setPurchaseModalSettings(purchaseItem);
    },
    [affiliateCode, gallery, site],
  );

  const sortedPrices = useMemo(() => purchaseOptions.sort((a, b) => a.price - b.price), [purchaseOptions]);

  const startingPrice = sortedPrices.length > 0 && sortedPrices[0].price;
  const currencyLeftSign = '$';
  const currencyRightSign = '';

  const purchasePanel = useMemo(() => {
    if (gallery.isStaffSelection) {
      return <MovieStaffSelectionPurchasePanel onPurchase={() => setShowPurchaseOptionsModal(true)} />;
    }

    if (gallery.isIntimateSelection) {
      return <MetartIntimatePurchasePanel onPurchase={() => setShowPurchaseOptionsModal(true)} />;
    }

    return (
      <MovieRegularPurchasePanel
        downloadsDisabled={gallery.downloadsDisabled || purchaseOptions.length === 0}
        galleryCount={config.galleryCount}
        movieCount={config.movieCount}
        onPurchase={() => setShowPurchaseOptionsModal(true)}
        startingPrice={`${currencyLeftSign}${startingPrice}${currencyRightSign}`}
        hasPPS={hasPPS}
        has4kResolution={has4kResolution}
      />
    );
  }, [
    config.galleryCount,
    config.movieCount,
    gallery.downloadsDisabled,
    gallery.isIntimateSelection,
    gallery.isStaffSelection,
    has4kResolution,
    hasPPS,
    purchaseOptions.length,
    startingPrice,
  ]);

  return (
    <PanelWrapper className={props.className}>
      {purchasePanel}

      <MoviePurchaseOptionsModal
        purchaseOptions={purchaseOptions}
        show={showPurchaseOptionsModal}
        onHide={() => setShowPurchaseOptionsModal(false)}
        onSelect={purchase}
      />

      <PurchaseGalleryDialog
        onHide={() => setPurchaseModalSettings(false)}
        price={purchaseModalSettings ? purchaseModalSettings.price : 0}
        purchaseError={ppsInfo.purchaseError}
        purchaseLoading={ppsInfo.purchaseLoading}
        purchaseRedirect={ppsInfo.purchaseRedirect}
        purchaseScene={user && user.email ? chargeByPreviousPurchase : purchaseScene}
        purchaseStatus={ppsInfo.purchaseStatus}
        quality={purchaseModalSettings ? purchaseModalSettings.title : ''}
        runtime={secondsToRuntime(gallery.media.runtime)}
        show={!!purchaseModalSettings}
        submitted={purchasing}
        thumbnail={getGalleryCover(contentCdnUrl, gallery)}
        wideThumbnail={getGalleryCover(contentCdnUrl, gallery, { wide: true })}
        title={gallery.name}
        type="movie"
        user={!!(user && user.email)}
        year={new Date(gallery.publishedAt).getUTCFullYear()}
        description={gallery.description}
        validating={ppsInfo.validating}
      />
    </PanelWrapper>
  );
}

const getItemBrand = (gallery: Movie, site: Site) => {
  if (gallery.isStaffSelection) return 'Staff Selection';
  if (gallery.isIntimateSelection) return 'MetArt Intimate';
  return site.name;
};

const PanelWrapper = styled.div`
  & > div {
    display: flex;
    flex-direction: column;
  }
`;
